import React, { useState, useEffect } from 'react';
import { Check, BookOpen, ChevronDown, ChevronUp, BookMarked, Heart, Footprints, PenTool, Edit3, Save, RefreshCw, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Devotion } from '../types';
import { Session } from '@supabase/supabase-js';
import { useNotes } from '../hooks/useNotes';
import { SpeechControls } from './SpeechControls';
import { SermonButton } from './SermonButton';
import { YouthButton } from './YouthButton';

interface DevotionCardProps {
  devotion: Devotion;
  onComplete: (id: number) => void;
  isSearchResult?: boolean;
  session: Session | null;
  onNavigate?: (direction: 'prev' | 'next') => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
  initialExpanded?: boolean;
}

function DevotionCard({ 
  devotion, 
  onComplete, 
  isSearchResult = false,
  session,
  onNavigate,
  hasPrevious = false,
  hasNext = false,
  initialExpanded = false
}: DevotionCardProps) {
  const [isExpanded, setIsExpanded] = useState(initialExpanded);
  const { notes, saveNote, updateNote, deleteNote } = useNotes(devotion.id);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteContent, setEditingNoteContent] = useState('');
  const [newNoteContent, setNewNoteContent] = useState('');

  // Update expansion state when initialExpanded prop changes
  useEffect(() => {
    setIsExpanded(initialExpanded);
  }, [initialExpanded]);

  // Construct full devotion text for speech
  const devotionText = `
    ${devotion.title}.
    
    Theme: ${devotion.theme}
    
    ${devotion.description}
    
    ${devotion.verse}
    
    ${devotion.authorQuote?.text ? `Quote: ${devotion.authorQuote.text} - ${devotion.authorQuote.author}` : ''}
    
    ${devotion.parable?.title ? `Parable: ${devotion.parable.title}
    ${devotion.parable.content}` : ''}
    
    Reflection Points:
    ${devotion.reflection && Array.isArray(devotion.reflection) 
      ? devotion.reflection.map(point => `• ${point}`).join('\n') 
      : ''}
    
    Action Steps:
    ${devotion.actionSteps && Array.isArray(devotion.actionSteps) 
      ? devotion.actionSteps.map(step => `• ${step}`).join('\n') 
      : ''}
    
    Prayer:
    ${devotion.prayer || ''}
  `;

  const handleAddNote = async () => {
    if (!newNoteContent.trim()) return;
    await saveNote(newNoteContent);
    setNewNoteContent('');
  };

  const handleUpdateNote = async (noteId: string) => {
    if (!editingNoteContent.trim()) return;
    await updateNote(noteId, editingNoteContent);
    setEditingNoteId(null);
    setEditingNoteContent('');
  };

  const startEditingNote = (noteId: string, content: string) => {
    setEditingNoteId(noteId);
    setEditingNoteContent(content);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 transition-colors">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2">{devotion.title}</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">{devotion.description}</p>
          
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-sm">
              Day {devotion.day}
            </span>
            <span className="px-3 py-1 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded-full text-sm">
              {devotion.section}
            </span>
          </div>
        </div>

        {session && (
          <button
            onClick={() => onComplete(devotion.id)}
            className={`ml-4 p-2 rounded-full transition-colors ${
              devotion.completed
                ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
            aria-label={devotion.completed ? "Mark as incomplete" : "Mark as complete"}
          >
            <Check className="w-6 h-6" />
          </button>
        )}
      </div>

      <div className="flex items-center justify-between mt-4">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
        >
          {isExpanded ? (
            <>
              <ChevronUp className="w-5 h-5 mr-2" />
              Hide Devotion
            </>
          ) : (
            <>
              <BookOpen className="w-5 h-5 mr-2" />
              Read Devotion
            </>
          )}
        </button>

        {!isSearchResult && onNavigate && (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onNavigate('prev')}
              disabled={!hasPrevious}
              className={`p-2 rounded-full ${
                hasPrevious
                  ? 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
              }`}
              aria-label="Previous devotion"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => onNavigate('next')}
              disabled={!hasNext}
              className={`p-2 rounded-full ${
                hasNext
                  ? 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
              }`}
              aria-label="Next devotion"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

      {isExpanded && (
        <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-6">
          <div className="flex flex-col space-y-2 mb-6">
            <SpeechControls text={devotionText} />
            <div className="flex space-x-2">
              <SermonButton devotionId={devotion.id} />
              <YouthButton devotionId={devotion.id} />
            </div>
          </div>

          <div className="space-y-6">
            {/* Theme */}
            <div className="bg-blue-50 dark:bg-blue-900/50 p-4 rounded-lg">
              <h3 className="font-semibold text-blue-800 dark:text-blue-300 mb-2">Theme</h3>
              <p className="text-blue-700 dark:text-blue-400">{devotion.theme}</p>
            </div>

            {/* Description */}
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <p className="text-gray-700 dark:text-gray-300">{devotion.description}</p>
            </div>

            {/* Verse */}
            <div className="bg-amber-50 dark:bg-amber-900/50 p-4 rounded-lg">
              <h3 className="font-semibold text-amber-800 dark:text-amber-300 mb-2">Scripture</h3>
              <p className="text-amber-700 dark:text-amber-300 italic">{devotion.verse}</p>
            </div>

            {/* Author Quote */}
            {devotion.authorQuote?.text && (
              <div className="bg-fuchsia-50 dark:bg-fuchsia-900/50 p-4 rounded-lg border-l-4 border-fuchsia-200 dark:border-fuchsia-600">
                <p className="text-fuchsia-800 dark:text-fuchsia-400 italic">"{devotion.authorQuote.text}"</p>
                {devotion.authorQuote.author && (
                  <p className="text-fuchsia-600 dark:text-fuchsia-400 text-right mt-2">— {devotion.authorQuote.author}</p>
                )}
              </div>
            )}

            {/* Parable */}
            {devotion.parable?.title && (
              <div className="bg-purple-50 dark:bg-purple-900/50 p-4 rounded-lg">
                <h3 className="font-semibold text-purple-800 dark:text-purple-300 mb-2">{devotion.parable.title}</h3>
                <p className="text-purple-700 dark:text-purple-400">{devotion.parable.content}</p>
              </div>
            )}

            {/* Reflection */}
            {devotion.reflection && Array.isArray(devotion.reflection) && devotion.reflection.length > 0 && (
              <div className="bg-emerald-50 dark:bg-emerald-900/50 p-4 rounded-lg">
                <h3 className="font-semibold text-emerald-800 dark:text-emerald-300 mb-2">Reflection</h3>
                <ul className="list-disc list-inside space-y-2">
                  {devotion.reflection.map((point, index) => (
                    <li key={index} className="text-emerald-700 dark:text-emerald-400">{point}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Action Steps */}
            {devotion.actionSteps && Array.isArray(devotion.actionSteps) && devotion.actionSteps.length > 0 && (
              <div className="bg-cyan-50 dark:bg-cyan-900/50 p-4 rounded-lg">
                <h3 className="font-semibold text-cyan-800 dark:text-cyan-300 mb-2">Action Steps</h3>
                <ul className="list-disc list-inside space-y-2">
                  {devotion.actionSteps.map((step, index) => (
                    <li key={index} className="text-cyan-700 dark:text-cyan-400">{step}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Prayer */}
            {devotion.prayer && (
              <div className="bg-indigo-50 dark:bg-indigo-900/50 p-4 rounded-lg">
                <h3 className="font-semibold text-indigo-800 dark:text-indigo-300 mb-2">Prayer</h3>
                <p className="text-indigo-700 dark:text-indigo-400">{devotion.prayer}</p>
              </div>
            )}
          </div>

          {session && (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
              <h3 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-200">Your Notes</h3>
              <div className="space-y-4">
                {notes && notes.map((note) => (
                  <div key={note.id} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    {editingNoteId === note.id ? (
                      <div className="space-y-2">
                        <textarea
                          value={editingNoteContent}
                          onChange={(e) => setEditingNoteContent(e.target.value)}
                          className="w-full p-2 border rounded resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600"
                          rows={3}
                        />
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleUpdateNote(note.id)}
                            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
                          >
                            <Save className="w-4 h-4 mr-1" />
                            Save
                          </button>
                          <button
                            onClick={() => {
                              setEditingNoteId(null);
                              setEditingNoteContent('');
                            }}
                            className="px-3 py-1 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-500 flex items-center"
                          >
                            <X className="w-4 h-4 mr-1" />
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{note.content}</p>
                        <div className="flex space-x-2 mt-2">
                          <button
                            onClick={() => startEditingNote(note.id, note.content)}
                            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 flex items-center text-sm"
                          >
                            <Edit3 className="w-4 h-4 mr-1" />
                            Edit
                          </button>
                          <button
                            onClick={() => deleteNote(note.id)}
                            className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 flex items-center text-sm"
                          >
                            <X className="w-4 h-4 mr-1" />
                            Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                <div className="space-y-2">
                  <textarea
                    value={newNoteContent}
                    onChange={(e) => setNewNoteContent(e.target.value)}
                    placeholder="Add a new note..."
                    className="w-full p-2 border rounded resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600 dark:placeholder-gray-500"
                    rows={3}
                  />
                  <button
                    onClick={handleAddNote}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
                  >
                    <PenTool className="w-4 h-4 mr-2" />
                    Add Note
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DevotionCard;